import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RELATED_TRAVELERS_QUERY_KEY } from '@/app/(main)/clients/create/const/const';
import { CLIENT_ID_QUERY_KEY } from '@/consts/const';
import { useFetch } from '@/hooks/useFetch';
import { useSearchQuery } from '@/hooks/useSearchQuery';
import type { ClientPreferenceData } from '@/types/clientsApiTypes';
import { CLIENT_DETAILS_QUERY_KEY, CLIENT_PREFERENCES_QUERY_KEY, CLIENT_QUERY_KEY } from '../../consts/client-detail';
import { CLIENT_GROUPED_QUERY_KEY } from '../useGetClientGroupedQuery';

interface UpdateClientPreferencesPayload {
  preference_data: Partial<ClientPreferenceData>;
}

interface UpdateClientPreferencesResponse {
  success: boolean;
  message: string;
}

export type UpdateClientPreferencesParams =
  | Partial<ClientPreferenceData>
  | { preference_data: Partial<ClientPreferenceData>; clientId?: number };

export const useUpdateClientPreferencesMutation = () => {
  const queryClient = useQueryClient();
  const [searchQuery] = useSearchQuery();
  const urlClientId = searchQuery.get(CLIENT_ID_QUERY_KEY);
  const { doFetch: updateClientPreferences } = useFetch<
    UpdateClientPreferencesPayload,
    UpdateClientPreferencesResponse
  >();

  return useMutation({
    mutationFn: (data: UpdateClientPreferencesParams) => {
      const preferenceData = 'preference_data' in data ? data.preference_data : data;
      const targetClientId = 'clientId' in data && data.clientId != null ? String(data.clientId) : urlClientId;
      return updateClientPreferences(`/api/client-preferences/${targetClientId}`, {
        method: 'PATCH',
        body: {
          preference_data: preferenceData,
        },
      });
    },
    onSuccess: (_data, variables) => {
      const urlId = urlClientId;
      queryClient.invalidateQueries({ queryKey: [CLIENT_PREFERENCES_QUERY_KEY, urlId] });
      queryClient.invalidateQueries({ queryKey: [CLIENT_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [CLIENT_DETAILS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [RELATED_TRAVELERS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [CLIENT_GROUPED_QUERY_KEY] });
      const targetId =
        typeof variables === 'object' && 'clientId' in variables && variables.clientId != null
          ? variables.clientId
          : null;
      if (targetId != null && String(targetId) !== urlId) {
        queryClient.invalidateQueries({ queryKey: [CLIENT_PREFERENCES_QUERY_KEY, String(targetId)] });
      }
    },
  });
};
